import styled from 'styled-components';
import media from 'styled-media-query';

import { Link } from 'react-router-dom';

import { StripeInvoiceFragment as StripeInvoice } from '../../gen/graphql';
import { InvoiceList } from './InvoiceList';
import { Loader } from '../molecules/Loader';
import Pager from './Pager';
import { AccountSettingsHeader } from '../atoms/AccountSettingsHeader';

interface InvoiceContainerProps {
  invoices: StripeInvoice[];
  loading: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  fetchNextInvoices: () => void;
  fetchPreviousInvoices: () => void;
  showTeamInvoiceLink?: boolean;
}

export const InvoiceContainer: React.FC<InvoiceContainerProps> = ({
  invoices,
  loading,
  hasNext,
  hasPrevious,
  fetchNextInvoices,
  fetchPreviousInvoices,
  showTeamInvoiceLink = false,
}) => {
  return (
    <>
      <Loader display={loading} />
      {invoices.length > 0 ? (
        <Container>
          <AccountSettingsHeader>ご請求履歴</AccountSettingsHeader>
          {showTeamInvoiceLink && (
            <Notion>
              法人の請求情報は
              <Link to="/team/invoice">こちら</Link>
              をご覧ください。
            </Notion>
          )}
          <InvoiceList invoices={invoices} />
          <Pager
            hasPrevious={hasPrevious}
            fetchPrevious={fetchPreviousInvoices}
            hasNext={hasNext}
            fetchNext={fetchNextInvoices}
          />
        </Container>
      ) : (
        <EmptyContainer>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18.6655 10.2401C16.9599 10.2401 15.5743 11.6257 15.5743 13.3345V50.6657C15.5743 52.3745 16.9599 53.7601 18.6687 53.7601H45.3375C47.0463 53.7601 48.4287 52.3745 48.4287 50.6689V23.5713H39.9998C37.2926 23.5713 35.0942 21.3761 35.0942 18.6657V10.2401H18.6655ZM52.2143 19.7122C52.3041 19.7981 52.3867 19.8913 52.4612 19.991C52.7495 20.3758 52.9087 20.8466 52.9087 21.3345V50.6689C52.9087 54.8513 49.5199 58.2401 45.3375 58.2401H18.6655C14.4831 58.2401 11.0911 54.8481 11.0911 50.6657V13.3345H11.0943C11.0943 9.15213 14.4863 5.76013 18.6687 5.76013H37.3343C37.9714 5.76013 38.5458 6.02522 38.9534 6.45127L52.2143 19.7122ZM45.2575 19.0913L39.5742 13.4081V18.6657C39.5742 18.9025 39.7662 19.0913 39.9998 19.0913H45.2575ZM29.7696 39.0722H26.8928C25.6832 39.0722 25.0784 39.4722 25.0784 40.269C25.0784 41.053 25.6832 41.4466 26.8928 41.4466H29.7696V42.4674C29.7696 44.1954 30.352 45.0626 31.52 45.0626C32.672 45.0626 33.248 44.1954 33.248 42.4674V41.4466H36.1376C37.3376 41.4466 37.9392 41.053 37.9392 40.269C37.9392 39.469 37.3376 39.069 36.1376 39.069H33.248V37.229H36.1344C37.3344 37.229 37.936 36.829 37.936 36.029C37.936 35.245 37.3344 34.8514 36.1344 34.8514H34.512L38.192 28.7778C38.4256 28.4034 38.592 28.0898 38.6976 27.8434C38.8032 27.6002 38.8576 27.325 38.8576 27.0274C38.8576 26.653 38.7264 26.3362 38.4576 26.077C38.1856 25.8178 37.84 25.6866 37.4144 25.6866C37.1168 25.6866 36.8576 25.7474 36.6432 25.8658C36.4256 25.9874 36.2432 26.1378 36.0992 26.3202C35.9552 26.5058 35.8048 26.7266 35.6512 26.9858C35.4976 27.2482 35.3536 27.4946 35.216 27.7346L31.52 33.8306L27.392 27.3634C27.0336 26.7746 26.7232 26.349 26.4512 26.0834C26.1824 25.8178 25.8304 25.6866 25.3952 25.6866C24.9696 25.6866 24.624 25.8178 24.352 26.0866C24.0864 26.3554 23.952 26.7106 23.952 27.1554C23.952 27.405 24 27.629 24.0928 27.8338C24.1856 28.0418 24.3872 28.381 24.6944 28.8578L28.5152 34.8546H26.8928C25.6832 34.8546 25.0784 35.2482 25.0784 36.0322C25.0784 36.8322 25.6832 37.2322 26.8928 37.2322H29.7696V39.0722Z"
              fill="black"
              fillOpacity="0.36"
            />
          </svg>
          <h2>お客様のご請求情報はありません</h2>
          {showTeamInvoiceLink && (
            <CenteredNotion>
              法人の請求情報は
              <Link to="/team/invoice">こちら</Link>
              をご覧ください。
            </CenteredNotion>
          )}
        </EmptyContainer>
      )}
    </>
  );
};

const Container = styled.section`
  width: 100%;
  max-width: 800px;
  margin: 1.25rem auto 0;
  padding: 2rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.125rem;
  box-sizing: border-box;

  ${media.lessThan('medium')`
    width: calc(100% + 2rem);
    margin: 1rem -1rem 0;
  `}
`;
const EmptyContainer = styled(Container)`
  padding: 3rem 2rem;

  svg {
    display: block;
    margin: 0 auto;
  }

  h2 {
    margin-top: 2rem;
    font-size: 1rem;
    line-height: 1.375rem;
    text-align: center;
    font-weight: bold;
  }

  ${media.lessThan('medium')`
    padding: 4rem 2rem;
  `}
`;
const Notion = styled.p`
  line-height: 1.33;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 0.5rem;

  a {
    font-size: inherit;
    color: #e2001b;
  }
`;
const CenteredNotion = styled(Notion)`
  font-size: 0.75rem;
  text-align: center;
`;
